import React, { useState, LegacyRef } from 'react';
/**
 * Base button component.
 *
 * Provides a consistent wrapper around a native `button` with optional
 * "waiting" behaviour.
 *
 * Waiting behaviour:
 * - `waitValue` forces the spinner + `waitText` to show (controlled by parent).
 * - `waitOnClick` enables local "busy" state that shows the spinner until the
 *   parent invokes the optional callback provided to `onClick`.
 *
 * Other behaviour:
 * - Click handler calls `preventDefault` and `stopPropagation`.
 * - When `disabled` is true, additional CSS classes are appended.
 */

type ButtonProps = {
  name: string;
  text: string;
  disabled?: boolean;
  onClick: (callback?: () => void) => void;
  waitText?: string;
  waitValue?: boolean;
  waitOnClick?: boolean;
  className?: string;
  ref?: LegacyRef<HTMLButtonElement>;
};

const Button = ({
  name,
  text,
  disabled,
  onClick,
  waitText,
  waitValue,
  waitOnClick,
  ref,
  className
}: ButtonProps): JSX.Element => {
  const [busy, setBusy] = useState(false);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    let callbackFunction;

    if (waitOnClick) {
      setBusy(true);

      callbackFunction = () => {
        setBusy(false);
      };
    }

    onClick(callbackFunction);
  };

  if (disabled === true) {
    className += ' button-disabled cms-btn-disabled';
  }

  const showWaiting = waitValue === true || (waitOnClick === true && busy);

  const buttonText = showWaiting ? waitText || '' : text;

  return (
    <button
      id={name}
      name={name}
      aria-label={buttonText}
      onClick={(e) => {
        handleClick(e);
      }}
      className={className}
      disabled={disabled}
      ref={ref}>
      {showWaiting && (
        <span
          className='spinner-border spinner-border-sm me-2'
          role='status'
          aria-hidden='true'
        />
      )}
      {buttonText}
    </button>
  );
};

export default Button;
